import { css } from '@emotion/react';
import { ListUserCard } from '@hotjar/comments/components/ListUserCard';
import { GenericShortListFilter } from '@hotjar/global-filters';
import { isUndefined, keyBy, uniqBy } from 'lodash-es';
import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { tokens } from '@hotjar/hotsauce';

function uniqueUsers(lookup: Record<string, User>, selected: User[]): User[] {
  return uniqBy(
    selected.map((x) => lookup[x.id]).filter((x) => !isUndefined(x)),
    'id',
  );
}

export interface User {
  avatar: React.ComponentProps<typeof ListUserCard>['avatar'];
  email: React.ComponentProps<typeof ListUserCard>['email'];
  id: number;
  name: string;
}

interface AuthorFilterProps {
  users: User[] | null;
  onApply: (users: User[]) => void;
  selectedUsers: User[];
}

export const AuthorFilter = ({
  selectedUsers = [],
  onApply,
  users,
}: AuthorFilterProps): JSX.Element | null => {
  const { t } = useTranslation();
  const sorted = useMemo(() => users?.sort((a, b) => a.name.localeCompare(b.name)), [users]);
  const lookupById = useMemo(() => keyBy(users, 'id'), [users]);

  // make sure all provided records are valid, since the user can inject arbitary
  // tokens in the URL, thus filtering here will reject invalid records
  const uniqUsers = uniqueUsers(lookupById, selectedUsers);

  const [currentValue, setCurrentValue] = useState<User[]>(uniqUsers);

  // reset the current values after route change
  useEffect(() => {
    setCurrentValue(uniqueUsers(lookupById, selectedUsers));
  }, [setCurrentValue, lookupById, selectedUsers]);

  if (sorted === null || sorted === undefined || sorted.length === 0) {
    return null;
  }

  const options = sorted.map((x) => ({
    value: x,
    label: x.name,
    content: (
      <ListUserCard key={x.id} name={x.name} email={x.email} avatar={x.avatar} focused={false} />
    ),
  }));

  const onChange = (val: User[] = []) => {
    setCurrentValue(val);
    onApply(val);
  };

  const onRemove = () => onChange([]);

  const dropdownStyles = css`
    & {
      max-height: 400px;
      overflow-y: auto;
      padding-left: ${tokens.spacer.x0};
    }
    & label {
      align-items: center;
    }
  `;

  return (
    <GenericShortListFilter
      filterName={t('highlights:filters.authors.filter-name')}
      label2={currentValue.length ? undefined : 'Any'}
      iconName="user_avatar"
      showDropdownCaret
      currentValue={currentValue}
      onRemove={currentValue.length ? onRemove : undefined}
      isInactive={currentValue.length === 0}
      optionList={options}
      onApply={onChange}
      renderLabel={false}
      dropdownCss={dropdownStyles}
    />
  );
};
